import { useCallback, useState } from "react";
import type {
  DigilockerInitiateSessionRequest,
  DigilockerInitiateSessionResponse,
  DigilockerGetReferenceRequest,
  DigilockerGetReferenceResponse,
} from "../types";
import { IDTO_API_ENDPOINTS, IDTO_BASE_URL } from "../constants";

interface UseIdtoApiReturn {
  initiateDigilockerSession: (
    request: DigilockerInitiateSessionRequest,
    authToken: string
  ) => Promise<DigilockerInitiateSessionResponse>;
  getDigilockerReference: (
    request: DigilockerGetReferenceRequest,
    authToken: string
  ) => Promise<DigilockerGetReferenceResponse>;
  isLoading: boolean;
  error: string | null;
  clearError: () => void;
}

export const useIdtoApi = (): UseIdtoApiReturn => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const initiateDigilockerSession = useCallback(
    async (
      request: DigilockerInitiateSessionRequest,
      authToken: string
    ): Promise<DigilockerInitiateSessionResponse> => {
      setIsLoading(true);
      setError(null);

      try {
        const response = await fetch(
          `${IDTO_BASE_URL}${IDTO_API_ENDPOINTS.DIGILOCKER_INITIATE_SESSION}`,
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${authToken}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify(request),
          }
        );

        const data = await response.json();

        if (!response.ok) {
          const errorMessage = data?.detail || `HTTP error! status: ${response.status}`;
          throw new Error(errorMessage);
        }

        if (data.status !== "success") {
          const errorMessage = data?.detail || "API request failed";
          throw new Error(errorMessage);
        }

        return data;
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : "An unknown error occurred";
        setError(errorMessage);
        throw new Error(errorMessage);
      } finally {
        setIsLoading(false);
      }
    },
    []
  );

  const getDigilockerReference = useCallback(
    async (
      request: DigilockerGetReferenceRequest,
      authToken: string
    ): Promise<DigilockerGetReferenceResponse> => {
      setIsLoading(true);
      setError(null);

      try {
        const response = await fetch(
          `${IDTO_BASE_URL}${IDTO_API_ENDPOINTS.DIGILOCKER_GET_REFERENCE}`,
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${authToken}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify(request),
          }
        );

        const data = await response.json();

        if (!response.ok) {
          const errorMessage = data?.detail || `HTTP error! status: ${response.status}`;
          throw new Error(errorMessage);
        }

        if (data.status !== "success") {
          const errorMessage = data?.detail || "API request failed";
          throw new Error(errorMessage);
        }

        return data;
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : "An unknown error occurred";
        setError(errorMessage);
        throw new Error(errorMessage);
      } finally {
        setIsLoading(false);
      }
    },
    []
  );

  return {
    initiateDigilockerSession,
    getDigilockerReference,
    isLoading,
    error,
    clearError,
  };
};
